import { useMutation } from '@apollo/client';
import { Button, Form, Space, Typography } from 'antd';
import React, { useEffect, useMemo } from 'react';
import {
  ASSET_CATEGORY,
  MODULE_TYPES,
  STATUS_TYPES
} from '../../../../../common/constants';
import SelectAsset from '../../../../assets/components/SelectAsset';
import { FORM_TYPES } from '../../../context/EditPageProvider';
import {
  CREATE_PAGE_MODULE,
  UPDATE_PAGE_MODULE
} from '../../../graphql/Mutations';
import { ModuleFields, Permissions, ShowFields } from './FormInputs';

const getInitialValues = () => ({
  title: '',
  description: '',
  status: STATUS_TYPES.PUBLISHED,
  permissions: [],
  settings: {
    playInLoop: true
  },
  config: {
    assets: []
  }
});

const SETTINGS = [
  {
    name: 'playInLoop',
    label: 'Play in loop',
    allowedTypes: [MODULE_TYPES.IMAGE_CAROUSEL]
  }
];

const ImageCarouselForm = ({
  form: { type: formType, moduleId, defaultValues, index: order },
  pageId,
  type,
  onCancel,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const isEdit = formType === FORM_TYPES.EDIT;
  const initialValues = useMemo(() => getInitialValues(), []);

  const MODULE_KEYS = {
    [MODULE_TYPES.IMAGE_CAROUSEL]: 'imageCarouselModule'
  };

  const CONFIG_TITLE = {
    [MODULE_TYPES.IMAGE_CAROUSEL]: 'Image Carousel Config'
  };

  useEffect(() => {
    if (moduleId && formType === FORM_TYPES.EDIT && form) {
      form.setFieldsValue({
        title: defaultValues?.title ?? '',
        description: defaultValues?.description ?? '',
        status: defaultValues?.status ?? STATUS_TYPES.DRAFT,
        permissions:
          defaultValues?.permissions?.map((value) => ({
            label: value,
            value
          })) ?? [],
        settings: {
          ...initialValues.settings,
          ...defaultValues?.moduleData?.settings
        },
        config: {
          ...defaultValues?.moduleData?.config,
          assets: defaultValues?.moduleData?.config?.images
        }
      });
    }
  }, [moduleId, formType, defaultValues, form, initialValues]);

  const [addEditModule, { loading }] = useMutation(
    isEdit ? UPDATE_PAGE_MODULE : CREATE_PAGE_MODULE
  );

  const handleSubmit = ({ config, settings, permissions, ...values }) => {
    const key = MODULE_KEYS[type];
    const imageCarouselIds = config?.assets?.map((image) => image?.id);

    const payload = {
      [key]: {
        settings,
        config: {
          imageIds: imageCarouselIds
        }
      },
      ...(!isEdit && {
        type,
        order: order + 1
      }),
      permissions: permissions?.map(({ value }) => value),
      status: values?.status ?? null,
      title: values?.title ?? null,
      description: values?.description ?? null
    };

    addEditModule({
      variables: { data: payload, id: isEdit ? moduleId : pageId }
    }).then(
      ({
        data: {
          addUpdatedPageModule: { pageModule }
        }
      }) => {
        onSuccess(pageModule);
      }
    );
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={initialValues}
    >
      <ModuleFields />
      <Space className="w-full" direction="vertical">
        <ShowFields settings={SETTINGS} type={type} />
      </Space>
      <Space className="w-full" direction="vertical">
        <Typography.Text>{CONFIG_TITLE[type]}</Typography.Text>
        <div>
          <Form.Item label="Images" name={['config', 'assets']}>
            <SelectAsset
              multiple
              modalTitle="Select Image"
              categoryKey={ASSET_CATEGORY.IMAGE}
              btnText="Image"
              dataSelector={({ id, url }) => ({
                id,
                url
              })}
            />
          </Form.Item>
        </div>
      </Space>
      <Permissions />
      <div className="d-flex button-section">
        <Space>
          <Form.Item>
            <Button
              disabled={loading}
              loading={loading}
              htmlType="submit"
              className="text-btn mr-8"
              type="text"
            >
              Save
            </Button>
          </Form.Item>
          <Form.Item>
            <Button
              type="text"
              disabled={loading}
              className="text-btn2"
              onClick={onCancel}
            >
              Cancel
            </Button>
          </Form.Item>
        </Space>
      </div>
    </Form>
  );
};

export default ImageCarouselForm;
